import styled from 'styled-components';
import { palette, theme } from 'styled-tools';

import { ChevronDown as chevronDown } from '@calm-web/icons';

import { minWidth } from '../../utils/mediaQueries';
import { units } from '../../utils/spacing';
import { fontFamily, fontSize, FontSizes, fontWeight, FontWeights } from '../../utils/typography';
import { ErrorContainer as InputErrorContainer, Label as InputLabel } from '../FormInput/styles';
import { CalmTheme, PortalTheme } from '../ThemeProvider';

export const Wrapper = styled.div`
	margin-bottom: ${units(1)};
	position: relative;
	width: 100%;
	z-index: 0;

	${minWidth('tablet')} {
		margin-bottom: ${units(2)};
	}
`;

export const InnerWrapper = styled.div`
	position: relative;
`;

interface SelectProps {
	$hasValue: boolean;
	$backgroundColor?: string;
	$textColor?: string;
	$provideSpaceForAnimatedLabel: boolean;
	$isValid?: boolean;
	$isError?: boolean;
}

const borderWidth = (
	props: SelectProps & {
		theme: typeof CalmTheme | typeof PortalTheme;
	},
	isFocus: boolean,
): string => {
	if (isFocus) {
		return props.theme.inputs.borderWidthFocus;
	}
	if (props.$isValid) {
		return props.theme.inputs.borderWidthSuccess;
	}
	if (props.$isError) {
		return props.theme.inputs.borderWidthError;
	}
	return props.theme.inputs.borderWidth;
};

export const Select = styled.select<SelectProps>`
	appearance: none;
	background: ${palette('white')};
	background-color: ${p => p.$backgroundColor || palette('white')};
	border-radius: ${theme('inputs.borderRadius')};
	border-width: ${props => borderWidth(props, false)};
	border-style: solid;
	border-color: ${props => {
		if (props.$isValid) {
			return palette('inputBorderSuccess');
		}
		if (props.$isError) {
			return palette('inputBorderError');
		}
		if (props.$hasValue) {
			return palette('inputBorderFull');
		}
		return palette('inputBorderEmpty');
	}};
	color: ${p => p.$textColor || palette(p.$hasValue ? 'inputText' : 'inputPlaceholder')(p)};
	${fontFamily};
	${fontSize(FontSizes.base)};
	${fontWeight(FontWeights.Medium)};
	height: ${units(6)};
	margin: 0;
	padding-top: ${p => (p.$provideSpaceForAnimatedLabel ? '16px' : '0')};
	padding-right: calc(${units(1.5)} + 1px - ${props => borderWidth(props, false)});
	padding-bottom: 0;
	padding-left: calc(${units(4.5)} + 1px - ${props => borderWidth(props, false)});
	width: 100%;

	${minWidth('mobileLarge')} {
		padding-top: ${p => (p.$provideSpaceForAnimatedLabel ? '19px' : '0')};
		height: ${units(7)};
	}

	&:focus {
		border-color: ${palette('inputBorderFocus')};
		border-width: ${props => borderWidth(props, true)};
		padding-right: calc(${units(1.5)} + 1px - ${props => borderWidth(props, true)});
		padding-left: calc(${units(4.5)} + 1px - ${props => borderWidth(props, true)});
		outline: none;
	}
`;

export const DownArrow = styled(chevronDown)<{ $hasValue: boolean }>`
	height: ${units(1)};
	left: ${units(1.5)};
	pointer-events: none;
	position: absolute;
	top: 50%;
	transform: translateY(-50%);
	width: ${units(1.75)};
	z-index: 1;

	& path {
		fill: ${p => palette(p.$hasValue ? 'inputText' : 'inputLabel')(p)};
	}
`;

export const Label = styled(InputLabel).attrs({ ['$hasIcon']: true })`
	transform: ${p => {
		// +1 for the border
		const x = 36 + 1;
		const y = (48 - 24) / 2; // (height - lineHeight) / 2
		return p.$isFocusedOrHasValue
			? `translate3d(${x}px, 5px, 0) scale(0.66)`
			: `translate3d(${x}px, ${y}px, 0)`;
	}};
	${minWidth('mobileLarge')} {
		transform: ${p => {
			// +1 for the border
			const x = 36 + 1;
			const y = (56 - 36) / 2; // (height - lineHeight) / 2
			return p.$isFocusedOrHasValue
				? `translate3d(${x}px, 8px, 0) scale(0.66)`
				: `translate3d(${x}px, ${y}px, 0)`;
		}};
	}
`;

export const ErrorContainer = styled(InputErrorContainer)``;
